import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import bgImage from "./img/scene-bondee-ville-indienne.jpg";
import Modal from "./Modal";

// Fonction pour traiter la signature
const processSignature = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    const img = new Image();

    reader.onload = () => {
      img.src = reader.result;
    };
    reader.onerror = () => reject("Erreur lecture fichier");

    img.onload = async () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      const nivGris = 40; // seuil de détection
      const margin = 20;

      let minX = canvas.width,
        minY = canvas.height,
        maxX = 0,
        maxY = 0;

      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const i = (y * canvas.width + x) * 4;
          const grayscale =
            0.3 * data[i] + 0.59 * data[i + 1] + 0.11 * data[i + 2];
          if (grayscale < nivGris) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }

      if (minX > maxX || minY > maxY) {
        return reject("Signature introuvable");
      }

      let cropWidth = maxX - minX + 1;
      let cropHeight = maxY - minY + 1;

      // Ajuster pour respecter ratio H/L ≤ 1:3
      const targetMinWidth = Math.max(cropWidth, Math.ceil((cropHeight / 1) * 3));
      const totalExtra = targetMinWidth - cropWidth;
      const extraLeft = Math.floor(totalExtra / 2);
      const extraRight = totalExtra - extraLeft;

      // Appliquer marges + extension pour respecter ratio
      minX = Math.max(0, minX - margin - extraLeft);
      maxX = Math.min(canvas.width - 1, maxX + margin + extraRight);
      minY = Math.max(0, minY - margin);
      maxY = Math.min(canvas.height - 1, maxY + margin);

      cropWidth = maxX - minX + 1;
      cropHeight = maxY - minY + 1;

      const cropCanvas = document.createElement("canvas");
      cropCanvas.width = cropWidth;
      cropCanvas.height = cropHeight;
      const cropCtx = cropCanvas.getContext("2d");

      cropCtx.drawImage(
        canvas,
        minX,
        minY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight
      );

      cropCanvas.toBlob(async (blob) => {
        if (!blob) return reject("Erreur toBlob");

        try {
          const compressed = await compressImage(
            new File([blob], file.name, { type: "image/jpeg" })
          );
          resolve(compressed);
        } catch (err) {
          reject("Erreur compression finale");
        }
      }, "image/jpeg", 0.85);
    };

    reader.readAsDataURL(file);
  });


// Fonction de compression d'image
const compressImage = (file, maxWidth = 800, quality = 0.7) =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const ratio = img.width / img.height
      const width = Math.min(maxWidth, img.width)
      const height = width / ratio
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)
      canvas.toBlob(
        blob => {
          if (blob) {
            resolve(new File([blob], file.name, { type: blob.type }))
          } else {
            reject(new Error("Compression failed"))
          }
        },
        'image/jpeg',
        quality
      )
    }
    img.onerror = () => reject(new Error("Image load error"))
    img.src = URL.createObjectURL(file)
  })

const Signatur = () => {
  const [signatures, setSignatures] = useState([])
  const [isDark, setIsDark] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const navigate = useNavigate()
  const [modal, setModal] = useState({ show: false, type: 'info', message: '', autoClose: 0, onClose: () => {} })


  // Détection mobile
  useEffect(() => {
    const checkMobile = () => {
      const ua = navigator.userAgent || navigator.vendor || window.opera
      setIsMobile(/android|iPad|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua))
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Détection thème
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    setIsDark(mq.matches)
    const onChange = e => setIsDark(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // Chargement initial
  useEffect(() => {
    const saved = localStorage.getItem('signatures')
    if (saved) {
      try { setSignatures(JSON.parse(saved)) }
      catch (e) { console.error('Load error:', e) }
    }
  }, [])

  // Traitement compress+lecture
  const processFiles = async files => {
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue
      let toRead = file
      try {
          toRead = await processSignature(file) }
      catch (err) { console.warn('Compression échouée, on lit original', err) }

      const reader = new FileReader()
      reader.onload = () => {
        if (!reader.result) return console.error('Lecture vide')
        const fileData = {
          id: Date.now() + Math.random(),
          name: toRead.name,
          size: toRead.size,
          type: toRead.type,
          data: reader.result
        }
        setSignatures(prev => {
          const updated = [...prev, fileData]
          localStorage.setItem('signatures', JSON.stringify(updated))
          return updated
        })
      }
      reader.readAsDataURL(toRead)
    }
  }
  

  const handleFileSelect = e => {
    e.preventDefault()
    if (e.target.files.length) processFiles(e.target.files)
  }
  const handleCameraCapture = e => {
    if (e.target.files[0]) processFiles([e.target.files[0]])
  }

  // Drag & drop
  const handleDragOver = e => { e.preventDefault(); setDragOver(true) }
  const handleDragLeave = e => { e.preventDefault(); setDragOver(false) }
  const handleDrop = e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length) processFiles(e.dataTransfer.files) }

  const clearAll = () => { setSignatures([]); localStorage.removeItem('signatures') }

  const uploadFiles = () => {
    if (signatures.length) {
      setModal({ show: true, type: 'success', message: 'Votre signature a bien été enregistrée', autoClose: 0, onClose: () => { setModal({ show: false }); navigate('/formuser') } })
    } else {
      setModal({ show: true, type: 'error', message: 'Veuillez sélectionner au moins un fichier avant de continuer.', autoClose: 0 })
    }
  }

  const formatFileSize = bytes => {
    if (!bytes) return '0 Bytes'
    const k = 1024, sizes = ['Bytes','KB','MB','GB'], i=Math.floor(Math.log(bytes)/Math.log(k))
    return (bytes/Math.pow(k,i)).toFixed(2)+' '+sizes[i]
  }

    const saveToLocalStorage = (newPassports) => {
        localStorage.setItem("signatures", JSON.stringify(newPassports))
    }

    const removeFile = (id) => {
        setSignatures(prev => {
            const newPassports = prev.filter(passport => passport.id !== id)
            saveToLocalStorage(newPassports)
            return newPassports
        })
    }

    const themeClasses = isDark ? {
        background: "min-h-screen bg-cover bg-center bg-no-repeat relative",
        backgroundImage: bgImage,
        overlay: "absolute inset-0 bg-black/60 backdrop-blur-sm",
        card: "bg-slate-800/95 backdrop-blur-xl border-slate-700/50",
        header: "bg-gradient-to-r from-orange-600 to-green-600",
        text: "text-slate-200",
        textMuted: "text-slate-400",
        textLight: "text-slate-300",
        uploadArea: dragOver ? "border-orange-400 bg-orange-900/40" : "border-slate-600 hover:border-orange-400 hover:bg-orange-900/20",
        uploadIcon: "bg-slate-700 text-slate-400 group-hover:bg-orange-700 group-hover:text-orange-300",
        button: "bg-gradient-to-r from-orange-600 to-green-600 hover:from-orange-700 hover:to-green-700 shadow-lg shadow-orange-500/25",
        cameraButton: "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg shadow-green-500/25",
        clearButton: "bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700",
        security: "text-slate-300",
        fileCard: "bg-slate-700/50 border-slate-600/50 hover:bg-slate-700/70"
    } : {
        background: "min-h-screen bg-cover bg-center bg-no-repeat relative",
        backgroundImage: bgImage,
        overlay: "absolute inset-0 bg-white/70 backdrop-blur-sm",
        card: "bg-white/90 backdrop-blur-xl border-white/50",
        header: "bg-gradient-to-r from-orange-600 to-green-600",
        text: "text-slate-700",
        textMuted: "text-slate-500",
        textLight: "text-slate-600",
        uploadArea: dragOver ? "border-orange-400 bg-orange-50/80" : "border-slate-300 hover:border-orange-400 hover:bg-orange-50/80",
        uploadIcon: "bg-white/80 text-slate-500 group-hover:bg-orange-100 group-hover:text-orange-600",
        button: "bg-gradient-to-r from-orange-600 to-green-600 hover:from-orange-700 hover:to-green-700 shadow-lg shadow-orange-500/20",
        cameraButton: "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg shadow-green-500/20",
        clearButton: "bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600",
        security: "text-slate-600",
        fileCard: "bg-white/70 border-white/50 hover:bg-white/80"
    }


    return (
        <>
        <div 
            className={themeClasses.background}
            style={{ backgroundImage: `url(${themeClasses.backgroundImage})` }}
        >
        <div className={`${themeClasses.background} flex items-center justify-center p-4`}>
            <div className="w-full max-w-2xl">
                <div className={`${themeClasses.card} rounded-3xl shadow-2xl overflow-hidden border`}>
                    <div className={`${themeClasses.header} px-8 py-6`}>
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                        </div>
                        <h2 className="text-2xl font-bold text-white text-center leading-tight">
                            Veuillez insérer une photos de signature ou scan de votre signature. <br /><br /> (La photo ou le scan doit être de bonne qualité)
                        </h2>
                    </div>

                    <div className="p-8">
                        <div className="mb-8">
                            <div className="mb-6">
                                <div className="relative mb-6">
                                    <input 
                                        onChange={handleFileSelect} 
                                        type="file"
                                        multiple
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                        accept=".jpg,.jpeg,.png"
                                    />
                                    <div 
                                        className={`border-2 border-dashed ${themeClasses.uploadArea} rounded-2xl p-12 text-center transition-all duration-300 group`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <div className="flex flex-col items-center">
                                            <div className={`w-20 h-20 ${themeClasses.uploadIcon} rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 transform group-hover:scale-110`}>
                                                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                </svg>
                                            </div>
                                            <p className={`${themeClasses.textLight} font-semibold mb-2 text-lg`}>
                                                Cliquez pour sélectionner
                                            </p>
                                            <p className={`${themeClasses.textMuted} mb-4`}>
                                                ou glissez-déposez vos fichiers ici
                                            </p>
                                            <div className={`px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/20 to-green-500/20 text-sm ${themeClasses.textMuted} border border-purple-200/30`}>
                                                JPG, PNG • Plusieurs fichiers acceptés
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Bouton pour prendre une photo (affiché uniquement sur mobile) */}
                                {isMobile && (
                                    <div className="mb-6">
                                        <div className={`text-center text-sm ${themeClasses.textMuted} mb-4 flex items-center justify-center gap-2`}>
                                            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-30"></div>
                                            <span>ou</span>
                                            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-30"></div>
                                        </div>
                                        <div className="relative">
                                            <input 
                                                onChange={handleCameraCapture}
                                                type="file"
                                                accept="image/*"
                                                capture="environment"
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                            />
                                            <button className={`w-full ${themeClasses.cameraButton} text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-emerald-200/50`}>
                                                <span className="flex items-center justify-center gap-3">
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    Prendre une photo
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Affichage des fichiers sélectionnés */}
                                {signatures.length > 0 && (
                                <div className="mb-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className={`text-lg font-semibold ${themeClasses.text}`}>
                                            Fichiers sélectionnés ({signatures.length})
                                        </h3>
                                        <button
                                            onClick={clearAll}
                                            className={`text-sm px-3 py-1 ${themeClasses.clearButton} text-white rounded-lg transition-all duration-200 hover:scale-105`}
                                        >
                                            Tout supprimer
                                        </button>


                                    </div>
                                    <div className="space-y-3 max-h-60 overflow-y-auto">
                                        {signatures.map((passport) => (
                                            <div key={passport.id} className={`${themeClasses.fileCard} border rounded-xl p-4 transition-all duration-200`}>
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-purple-500 to-blue-500 flex-shrink-0">
                                                        <img 
                                                            src={passport.data} 
                                                            alt={passport.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className={`font-medium ${themeClasses.text} truncate`}>
                                                            {passport.name}
                                                        </p>
                                                        <p className={`text-sm ${themeClasses.textMuted}`}>
                                                            {formatFileSize(passport.size)}
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={() => removeFile(passport.id)}
                                                        className="w-8 h-8 rounded-full bg-red-500/20 text-red-500 hover:bg-red-500/30 transition-colors flex items-center justify-center flex-shrink-0"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            </div>
                        </div>

                        <button
                            onClick={uploadFiles}
                            disabled={signatures.length === 0}
                            className={`w-full ${themeClasses.button} text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-blue-200/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                        >
                            <span className="flex items-center justify-center gap-3">
                                Continuer
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>

                <div className="mt-8 text-center">
                    <div className={`inline-flex items-center gap-3 px-6 py-3 ${themeClasses.fileCard} rounded-full border backdrop-blur-sm`}>
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span className={`text-sm font-medium ${themeClasses.security}`}>
                            Vos documents sont stockés localement et sécurisés
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
    {modal.show && (
      <Modal
        type={modal.type}
        message={modal.message}
        autoClose={modal.autoClose}
        onClose={modal.onClose}
      />
    )}
    </>
    )
}

export default Signatur